import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Info, TriangleAlert } from 'lucide-react';
import { api } from '../../lib/api';
import type { CollectionTypeDef } from '../../lib/types';
import { pickLoc } from '../../lib/collections';

type Row = {
  key: string;
  singularEs: string;
  pluralEs: string;
  singularEn: string;
  pluralEn: string;
  saving: boolean;
  saved: boolean;
};

function toRow(t: CollectionTypeDef): Row {
  return {
    key: t.key,
    singularEs: pickLoc(t.labels.singular, 'es') ?? '',
    pluralEs:   pickLoc(t.labels.plural,   'es') ?? '',
    singularEn: pickLoc(t.labels.singular, 'en') ?? '',
    pluralEn:   pickLoc(t.labels.plural,   'en') ?? '',
    saving: false,
    saved:  false,
  };
}

export default function CollectionLabelsTab({ onSaved }: { onSaved?: () => void } = {}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.collectionTypes.list()
      .then((types) => setRows(types.map(toRow)))
      .catch(() => setError('No se pudieron cargar los nombres de las secciones. Intenta recargar la página.'))
      .finally(() => setLoading(false));
  }, []);

  function update(key: string, field: keyof Row, value: string) {
    setRows((prev) => prev.map((r) => r.key === key ? { ...r, [field]: value, saved: false } : r));
  }

  async function save(key: string) {
    const row = rows.find((r) => r.key === key);
    if (!row) return;
    if (!row.singularEs.trim() || !row.pluralEs.trim()) {
      setError('Escribe el nombre singular y plural en español antes de guardar.');
      return;
    }
    setError('');
    setRows((prev) => prev.map((r) => r.key === key ? { ...r, saving: true } : r));
    try {
      await api.collectionTypes.updateLabels(key, {
        singular: { es: row.singularEs.trim(), en: row.singularEn.trim() },
        plural:   { es: row.pluralEs.trim(),   en: row.pluralEn.trim()   },
      });
      setRows((prev) => prev.map((r) => r.key === key ? { ...r, saving: false, saved: true } : r));
      onSaved?.();
      setTimeout(() => setRows((prev) => prev.map((r) => r.key === key ? { ...r, saved: false } : r)), 2000);
    } catch (e) {
      setError('No se pudieron guardar los nombres. Intenta de nuevo.');
      setRows((prev) => prev.map((r) => r.key === key ? { ...r, saving: false } : r));
    }
  }

  if (loading) return <p className="text-sm text-ui-text-muted">Cargando nombres…</p>;
  if (rows.length === 0) {
    if (error) {
      return (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-500/25 bg-red-500/10 px-4 py-3">
          <TriangleAlert size={16} className="mt-0.5 shrink-0 text-red-400" />
          <p className="text-sm text-ui-text-muted">{error}</p>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 rounded-lg border border-ui-border bg-ui-bg px-4 py-4">
        <Info size={17} className="mt-0.5 shrink-0 text-ui-accent" />
        <div>
          <p className="text-sm font-medium text-ui-text">Aún no tienes secciones de contenido activadas</p>
          <p className="mt-1 text-sm text-ui-text-muted">
            Usa “Añadir sección” en el menú lateral para activar una sección de contenido. Después podrás adaptar aquí sus nombres a tu negocio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ui-text-muted">
        Personaliza el nombre de cada sección de contenido según tu negocio. Por ejemplo, cambia "Equipo" a "Abogados" o "Notarios".
      </p>
      {error && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2.5">
          <TriangleAlert size={15} className="mt-0.5 shrink-0 text-red-400" />
          <p className="text-sm text-ui-text-muted">{error}</p>
        </div>
      )}
      {rows.map((row) => (
        <div key={row.key} className="bg-ui-bg border border-ui-border rounded-lg p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">{row.key}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ui-text-muted mb-1">Singular (ES)</label>
              <input
                value={row.singularEs}
                onChange={(e) => update(row.key, 'singularEs', e.target.value)}
                placeholder="ej. Abogado"
                className="w-full bg-ui-surface border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-ui-text-muted mb-1">Plural (ES)</label>
              <input
                value={row.pluralEs}
                onChange={(e) => update(row.key, 'pluralEs', e.target.value)}
                placeholder="ej. Abogados"
                className="w-full bg-ui-surface border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-ui-text-muted mb-1">Singular (EN)</label>
              <input
                value={row.singularEn}
                onChange={(e) => update(row.key, 'singularEn', e.target.value)}
                placeholder="ej. Lawyer"
                className="w-full bg-ui-surface border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-ui-text-muted mb-1">Plural (EN)</label>
              <input
                value={row.pluralEn}
                onChange={(e) => update(row.key, 'pluralEn', e.target.value)}
                placeholder="ej. Lawyers"
                className="w-full bg-ui-surface border border-ui-border rounded px-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <motion.button
              type="button"
              onClick={() => save(row.key)}
              disabled={row.saving}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-ui-accent text-ui-bg rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {row.saved
                ? <><Check className="w-3.5 h-3.5" /> Guardado</>
                : row.saving ? 'Guardando…' : 'Guardar'}
            </motion.button>
          </div>
        </div>
      ))}
    </div>
  );
}
